import React, { useState, useEffect } from 'react';
import { Modal, Box, Typography, TextField, Button, Alert, Select, MenuItem } from '@mui/material';
import { ResponsableObraEnum } from '../../consts/avisoObraTypes';

interface ContratistaModalProps {
    open: boolean;
    onClose: () => void;
    onSubmit: (data: any) => void;
    editData?: any;
    idTipoResolucion?: number;
}

const ContratistaModal: React.FC<ContratistaModalProps> = ({
    open,
    onClose,
    onSubmit,
    editData,
    idTipoResolucion
}) => {
    const [rol, setRol] = useState<string>('');
    const [cuit, setCuit] = useState<string>('');
    const [razonSocial, setRazonSocial] = useState<string>('');
    const [error, setError] = useState<string | null>(null);

    const getOpcionesRol = (): { id: ResponsableObraEnum; nombre: string }[] => {
        switch (idTipoResolucion) {
            case 3:
                return [{ id: ResponsableObraEnum.SUBCONTRATISTA, nombre: 'SUBCONTRATISTA' }];
            case 4:
                return [
                    { id: ResponsableObraEnum.COMITENTE, nombre: 'COMITENTE' },
                    {
                        id: ResponsableObraEnum.CONTRATISTA_PRINCIPAL,
                        nombre: 'CONTRATISTA PRINCIPAL'
                    }
                ];
            default:
                return [
                    { id: ResponsableObraEnum.SUBCONTRATISTA, nombre: 'SUBCONTRATISTA' },
                    { id: ResponsableObraEnum.COMITENTE, nombre: 'COMITENTE' },
                    {
                        id: ResponsableObraEnum.CONTRATISTA_PRINCIPAL,
                        nombre: 'CONTRATISTA PRINCIPAL'
                    }
                ];
        }
    };

    useEffect(() => {
        if (editData) {
            setCuit(editData.cuit || editData.cuitCuilCuip || '');

            setRazonSocial(editData.razonSocial || '');

            const opciones = getOpcionesRol();

            let rolEncontrado: { id: ResponsableObraEnum; nombre: string } | undefined;

            if (editData.tipoEmpresa) {
                rolEncontrado = opciones.find((o) => o.id === editData.tipoEmpresa);
            } else if (editData.tipoTexto) {
                rolEncontrado = opciones.find((o) => o.nombre === editData.tipoTexto);
            }

            setRol(rolEncontrado ? rolEncontrado.id.toString() : '');
        } else {
            const opciones = getOpcionesRol();
            if (opciones.length === 1) {
                setRol(opciones[0].id.toString());
            } else {
                setRol('');
            }
            setCuit('');
            setRazonSocial('');
        }
    }, [editData, idTipoResolucion]);

    const handleCuitChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const val = event.target.value;
        if (/^\d*$/.test(val) && val.length <= 11) setCuit(val);
    };

    const handleSubmit = () => {
        if (!rol || !cuit || !razonSocial) {
            setError('Debes completar todos los campos');
            return;
        }

        if (cuit.length !== 11) {
            setError('El CUIT debe tener 11 dígitos');
            return;
        }

        const selected = getOpcionesRol().find((o) => o.id.toString() === rol);
        if (!selected) {
            setError('Debes seleccionar un rol válido');
            return;
        }

        onSubmit({
            cuitCuilCuip: cuit,
            tipoEmpresa: selected.id,
            razonSocial: `${razonSocial} - ${selected.nombre}`
        });

        setRol('');
        setCuit('');
        setRazonSocial('');
        setError(null);
        onClose();
    };

    return (
        <Modal open={open} onClose={onClose}>
            <Box sx={modalStyle}>
                <Box sx={{ maxHeight: '100vh', overflowY: 'auto', padding: '16px' }}>
                    <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                        Responsable de la Obra
                    </Typography>

                    <Select
                        value={rol}
                        onChange={(e) => setRol(e.target.value)}
                        fullWidth
                        sx={{ mt: 2 }}
                        displayEmpty
                    >
                        <MenuItem disabled value="">
                            Seleccionar
                        </MenuItem>
                        {getOpcionesRol().map((op) => (
                            <MenuItem key={op.id} value={op.id.toString()}>
                                {op.nombre}
                            </MenuItem>
                        ))}
                    </Select>

                    <TextField
                        label="CUIT"
                        value={cuit}
                        onChange={handleCuitChange}
                        fullWidth
                        sx={{ mt: 2 }}
                        inputProps={{ maxLength: 11 }}
                        required
                    />

                    <TextField
                        label="Razón Social"
                        value={razonSocial}
                        onChange={(e) => setRazonSocial(e.target.value)}
                        fullWidth
                        sx={{ mt: 2 }}
                        required
                    />

                    {error && (
                        <Alert severity="error" sx={{ mt: 2 }}>
                            {error}
                        </Alert>
                    )}

                    <Box sx={{ mt: 3, textAlign: 'right' }}>
                        <Button onClick={onClose} sx={{ mr: 2 }}>
                            Cancelar
                        </Button>
                        <Button onClick={handleSubmit} variant="contained" color="primary">
                            Guardar
                        </Button>
                    </Box>
                </Box>
            </Box>
        </Modal>
    );
};

const modalStyle = {
    position: 'absolute' as const,
    height: '100%',
    width: '40%',
    bgcolor: 'background.paper',
    boxShadow: 24,
    borderRadius: 1,
    p: 1,
    right: '0px'
};

export default ContratistaModal;
